import React, { useState, useEffect, useContext } from "react";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import { API_BASE_URL } from "../../environment";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { AuthContext } from "../../context/authcontext";
import Pattern from "../../components/ui/Pattern";
import { isMobileOrTabletDevice } from "../../utils/deviceDetection";

type Department = {
  department_id: number;
  department_display_name: string;
  description: string | null;
};

type PasswordField = "password" | "confirmPassword";

const Register = () => {
  const navigate = useNavigate();
  const route = all_routes;
  const MySwal = withReactContent(Swal);
  const { auth } = useContext(AuthContext);

  useEffect(() => {
    if (auth) {
      navigate(route.dealsDashboard, { replace: true });
      return;
    }
  }, [auth, navigate, route.dealsDashboard]);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    organization_name: "",
    organization_domain: "",
    department_id: 0,
    password: "",
    confirmPassword: "",
    agreeTOS: true,
  });
  const [showPattern] = useState(() => !isMobileOrTabletDevice());
  const [departments, setDepartments] = useState<Department[]>([]);
  const [depsLoading, setDepsLoading] = useState(true);
  const [depsError, setDepsError] = useState<string | null>(null);
  const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
  const passwordsMatch =
    form.confirmPassword.length > 0 && form.password === form.confirmPassword;
  const DOMAIN_REGEX =
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
  useEffect(() => {
    const loadDeps = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/department/`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const data: Department[] = await res.json();
        setDepartments(data);
      } catch (err: any) {
        console.error("Failed to load departments:", err);
        setDepsError("Could not load departments. Please try again.");
      } finally {
        setDepsLoading(false);
      }
    };
    loadDeps();
  }, []);

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((p) => ({
      ...p,
      [field]: !p[field],
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const newValue =
      type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : name === "department_id"
          ? Number(value)
          : value;
    setForm((f) => ({ ...f, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (form.password !== form.confirmPassword) {
      MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Passwords do not match.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }
    if (!PASSWORD_REGEX.test(form.password)) {
      MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title:
          "Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.",
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
      });
      return;
    }

    if (!form.agreeTOS) {
      MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "You must agree to Terms & Privacy.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    if (form.department_id === 0) {
      MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Please select a department.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }
    if (!DOMAIN_REGEX.test(form.organization_domain)) {
      MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Please enter a valid domain (e.g. example.com).",
        showConfirmButton: false,
        timer: 3500,
        timerProgressBar: true,
      });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/signup/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          organization_name: form.organization_name,
          organization_domain: form.organization_domain,
          department_id: form.department_id,
          password: form.password,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        let rawMessage = "Signup failed";

        if (typeof data === "string") {
          const match = data.match(/string='([^']+)'/);
          if (match?.[1]) rawMessage = match[1];
          else rawMessage = data;
        } else if (Array.isArray(data) && data[0]?.string) {
          rawMessage = data[0].string;
        } else if (data.message) {
          rawMessage = data.message;
        }
        const normalized = rawMessage.toLowerCase();

        let finalMessage = "Signup failed. Please try again.";
        if (normalized.includes("email") && normalized.includes("already")) {
          finalMessage =
            "This email is already in use. Please try another one.";
        } else if (
          normalized.includes("password") &&
          normalized.includes("short")
        ) {
          finalMessage =
            "Your password is too short. Minimum 8 characters required.";
        }

        throw new Error(finalMessage);
      }

      navigate(route.emailVerification, {
        state: { email: form.email },
      });
    } catch (err: any) {
      MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: err.message || "Signup failed",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setLoading(false);
    }
  };
  const domainValid =
    form.organization_domain.length > 0 &&
    DOMAIN_REGEX.test(form.organization_domain);
  return (
    <div className="account-content">
      <div className="login-wrapper login-new position-relative overflow-hidden w-100 min-vh-100">
        {showPattern && (
          <div className="position-absolute w-100 h-100 top-0 start-0 pointer-events-none" style={{ zIndex: 0 }}><Pattern /></div>
        )}
        <div className="login-content user-login w-100 position-relative" style={{ zIndex: 1 }}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-10 col-xl-8">
                <form className="login-user-info" onSubmit={handleSubmit}>
                  <div className="text-center">
                    <img
                      src="/assets/img/Logo.png"
                      alt="Logo"
                      style={{
                        width: "200px",
                        height: "auto",
                      }}
                    />
                  </div>

                  {error && <div className="alert alert-danger">{error}</div>}

                  <div className="row">
                    {/* First Name */}
                    <div className="col-md-6 mb-3">
                      <label className="col-form-label">First Name</label>
                      <div className="position-relative">
                        <span className="input-icon-addon">
                          <i className="ti ti-user" />
                        </span>
                        <input
                          name="first_name"
                          value={form.first_name ?? ""}
                          onChange={handleChange}
                          placeholder="Enter first name"
                          type="text"
                          className="form-control"
                          required
                        />
                      </div>
                    </div>

                    {/* Last Name */}
                    <div className="col-md-6 mb-3">
                      <label className="col-form-label">Last Name</label>
                      <div className="position-relative">
                        <span className="input-icon-addon">
                          <i className="ti ti-user" />
                        </span>
                        <input
                          name="last_name"
                          value={form.last_name ?? ""}
                          onChange={handleChange}
                          placeholder="Enter last name"
                          type="text"
                          className="form-control"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    {/* Email */}
                    <div className="col-md-6 mb-3">
                      <label className="col-form-label">Email Address</label>
                      <div className="position-relative">
                        <span className="input-icon-addon">
                          <i className="ti ti-mail" />
                        </span>
                        <input
                          name="email"
                          value={form.email ?? ""}
                          onChange={handleChange}
                          placeholder="Enter email"
                          type="email"
                          className="form-control"
                          required
                        />
                      </div>
                    </div>

                    {/* Organization Name */}
                    <div className="col-md-6 mb-3">
                      <label className="col-form-label">Organization Name</label>
                      <div className="position-relative">
                        <span className="input-icon-addon">
                          <i className="ti ti-building" />
                        </span>
                        <input
                          name="organization_name"
                          value={form.organization_name ?? ""}
                          onChange={handleChange}
                          type="text"
                          className="form-control"
                          placeholder="Enter organization name"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    {/* Organization Domain */}
                    <div className="col-md-6 mb-3">
                      <label className="col-form-label">
                        Organization Domain
                      </label>
                      <div className="position-relative">
                        <span className="input-icon-addon">
                          <i className="ti ti-world-cog" />
                        </span>
                        <input
                          name="organization_domain"
                          value={form.organization_domain ?? ""}
                          onChange={handleChange}
                          type="text"
                          className="form-control"
                          placeholder="Enter organization domain"
                          pattern="(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}"
                          title="e.g. example.com or sub.domain.co.uk"
                          required
                        />
                        {form.organization_domain && (
                          <small
                            className={
                              domainValid ? "text-success" : "text-danger"
                            }
                          >
                            {domainValid
                              ? ""
                              : "Enter a valid domain (e.g. example.com) "}
                          </small>
                        )}
                      </div>
                    </div>

                    {/* Department */}
                    <div className="col-md-6 mb-3">
                      <label className="col-form-label">Department</label>
                      <div className="position-relative mb-2">
                        <span className="input-icon-addon">
                          <i className="ti ti-users" />
                        </span>
                        {depsLoading ? (
                          <div>Loading departments…</div>
                        ) : depsError ? (
                          <div className="text-danger">{depsError}</div>
                        ) : (
                          <select
                            name="department_id"
                            value={form.department_id}
                            onChange={handleChange}
                            className="form-control"
                            required
                          >
                            <option value={0}>Select Department</option>
                            {departments.map((d) => (
                              <option
                                key={d.department_id}
                                value={d.department_id}
                              >
                                {d.department_display_name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    {/* Password */}
                    <div className="col-md-6 mb-3">
                      <label className="col-form-label">Password</label>
                      <div className="pass-group">
                        <input
                          name="password"
                          type={passwordVisibility.password ? "text" : "password"}
                          value={form.password ?? ""}
                          placeholder="Enter password"
                          onChange={handleChange}
                          className="pass-input form-control"
                          required
                        />
                        <span
                          className={`ti toggle-password ${passwordVisibility.password ? "ti-eye" : "ti-eye-off"
                            }`}
                          onClick={() => togglePasswordVisibility("password")}
                        />
                      </div>
                    </div>

                    {/* Confirm Password */}
                    <div className="col-md-6 mb-3">
                      <label className="col-form-label">Confirm Password</label>
                      <div className="pass-group">
                        <input
                          name="confirmPassword"
                          type={
                            passwordVisibility.confirmPassword
                              ? "text"
                              : "password"
                          }
                          value={form.confirmPassword ?? ""}
                          onChange={handleChange}
                          placeholder="Enter confirm password"
                          className="pass-input form-control"
                          required
                        />
                        <span
                          className={`ti toggle-password ${passwordVisibility.confirmPassword
                            ? "ti-eye"
                            : "ti-eye-off"
                            }`}
                          onClick={() =>
                            togglePasswordVisibility("confirmPassword")
                          }
                        />
                      </div>

                      {/* live-match feedback */}
                      {form.confirmPassword && (
                        <small
                          className={
                            passwordsMatch ? "text-success" : "text-danger"
                          }
                        >
                          {passwordsMatch
                            ? "Passwords match"
                            : "Passwords do not match"}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Terms */}
                  <div className="mb-3">
                    <div className="form-check form-check-md">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="agreeTOS"
                        checked={!!form.agreeTOS}
                        onChange={handleChange}
                        id="checkbox-md"
                      />
                      <label className="form-check-label" htmlFor="checkbox-md">
                        I agree to the{" "}
                        <Link
                          to="https://gocloudee.com/privacy-policy"
                          className="text-primary link-hover"
                          target="_blank"
                        >
                          Terms &amp; Privacy
                        </Link>
                      </label>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="mb-3">
                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={loading}
                    >
                      <i
                        className="bi bi-person-check-fill me-2"
                        style={{ fontSize: "1rem" }}
                      />
                      {loading ? "Signing Up…" : "Sign Up"}
                    </button>
                  </div>

                  <div className="text-center">
                    <h6>
                      Already have an account? &nbsp;
                      <Link to={route.login} className="text-purple link-hover">
                        Sign In Instead
                      </Link>
                    </h6>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
